import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import '../style.css';
import {
  fadeUp,
  fadeLeft,
  fadeRight,
  staggerContainer,
  scaleFade,
  viewport,
} from '../components/useScrollAnimation';
import { PiCursorClickFill } from "react-icons/pi";

/* ── DATA ── */

const highlights = [
  {
    emoji: '💰',
    title: 'Penghasilan Hingga Rp 514.222.080/Tahun',
    desc: 'Allowance $14.81–$19/jam. Bekerja di hotel mewah Amerika dengan standar gaji kelas dunia.',
  },
  {
    emoji: '🏨',
    title: 'Partner Luxury Hotel',
    desc: 'Penempatan di Marriott International, Accor Group, InterContinental Hotels Group (IHG), dan Hyatt Hotels Corporation.',
  },
  {
    emoji: '🛂',
    title: 'Resmi & Terjamin',
    desc: 'Proses pengurusan visa dibantu oleh NIEC. Program 6-18 bulan termasuk akomodasi.',
  },
  {
    emoji: '🎓',
    title: 'Pengalaman Kerja Bertaraf Internasional',
    desc: 'Bangun CV impresif dengan pengalaman langsung di hotel bintang 5 Amerika — nilai tambah yang tak ternilai untuk karier globalmu.',
  },
  {
    emoji: '🤝',
    title: 'Pendampingan Penuh NIEC',
    desc: 'Dari persiapan dokumen, pelatihan bahasa Inggris, mock interview, hingga keberangkatan — tim NIEC mendampingimu di setiap langkah.',
  },
  {
    emoji: '🌎',
    title: 'Jaringan Alumni Global',
    desc: 'Bergabung dengan komunitas 300+ alumni NIEC yang telah berhasil berkarier di industri perhotelan internasional.',
  },
];

const positions = [
  {
    badge: 'Culinary',
    title: 'Culinary Kitchen Rotation',
    duration: 'Posisi Tersedia',
    tagline: 'Bekerja di dapur hotel berbintang lima, membuat hidangan berkualitas tinggi di Amerika Serikat.',
    details: [
      { icon: 'fa-solid fa-utensils', label: 'Divisi', value: 'Culinary' },
      { icon: 'fa-solid fa-clock', label: 'Durasi', value: '5 – 18 Bulan' },
      { icon: 'fa-solid fa-dollar-sign', label: 'Allowance', value: '$14.81 – $19 / jam' },
      { icon: 'fa-solid fa-hotel', label: 'Tipe Hotel', value: 'Bintang 5 Amerika' },
    ],
    income: 'Rp 514.222.080',
    incomeNote: 'Estimasi penghasilan per tahun',
    accent: false,
  },
  {
    badge: 'F&B',
    title: 'Food & Beverage Rotational',
    duration: 'Posisi Tersedia',
    tagline: 'Bekerja di restoran fine dining, bar, dan banquet hall hotel berbintang lima.',
    details: [
      { icon: 'fa-solid fa-utensils', label: 'Divisi', value: 'F&B Service / Culinary' },
      { icon: 'fa-solid fa-clock', label: 'Durasi', value: '5 – 18 Bulan' },
      { icon: 'fa-solid fa-dollar-sign', label: 'Allowance', value: '$14.81 – $19 / jam' },
      { icon: 'fa-solid fa-hotel', label: 'Tipe Hotel', value: 'Bintang 5 Amerika' },
    ],
    income: 'Rp 514.222.080',
    incomeNote: 'Estimasi penghasilan per tahun',
    accent: false,
  },
  {
    badge: 'Front Office',
    title: 'Front Desk Rotation',
    duration: 'Posisi Tersedia',
    tagline: 'Berkarier di Front Office, Housekeeping, & Guest Relations hotel ternama dunia.',
    details: [
      { icon: 'fa-solid fa-bed', label: 'Divisi', value: 'Front Office / Housekeeping' },
      { icon: 'fa-solid fa-clock', label: 'Durasi', value: '5 – 18 Bulan' },
      { icon: 'fa-solid fa-dollar-sign', label: 'Allowance', value: '$14.81 – $19 / jam' },
      { icon: 'fa-solid fa-hotel', label: 'Tipe Hotel', value: 'Bintang 5 Amerika' },
    ],
    income: 'Rp 514.222.080',
    incomeNote: 'Estimasi penghasilan per tahun',
    accent: false,
  },
];

const requirements = [
  {
    icon: 'fa-solid fa-calendar',
    label: 'Usia',
    detail: '18 – 34 tahun pada saat mendaftar.',
  },
  {
    icon: 'fa-solid fa-comments',
    label: 'Bahasa Inggris',
    detail: 'Mampu berkomunikasi aktif dalam bahasa Inggris (spoken & written).',
  },
  {
    icon: 'fa-solid fa-graduation-cap',
    label: 'Pendidikan',
    detail: 'Minimal D1 bidang Perhotelan, Pariwisata, atau terkait.',
  },
  {
    icon: 'fa-solid fa-file-lines',
    label: 'Dokumen',
    detail: 'CV, Ijazah + Transkrip, Paspor Aktif, KTP, & Sertifikat Kerja.',
  },
];

const steps = [
  {
    id: "1",
    title: "Pengumpulan Dokumen",
    description:
      "Lengkapi seluruh dokumen yang dibutuhkan sebagai syarat awal pendaftaran program.",
  },
  {
    id: "2",
    title: "Pembayaran Administration Fee 1",
    description:
      "Lakukan pembayaran biaya administrasi tahap pertama untuk memulai proses seleksi.",
  },
  {
    id: "3",
    title: "Deep Hire Preparation",
    description:
      "Ikuti sesi persiapan berupa kisi-kisi, latihan, dan simulasi sebelum mengikuti Deep Hire.",
  },
  {
    id: "4",
    title: "Memilih Open Position List (OPL)",
    description:
      "Pilih posisi yang sesuai dengan kemampuan dan minat dari daftar Open Position List.",
  },
  {
    id: "5",
    title: "Interview dengan Host Company",
    description:
      "Lakukan wawancara secara online dengan host company untuk mendapatkan penempatan.",
  },
  {
    id: "6",
    title: "Pembayaran Program",
    description:
      "Meliputi Program Fee, Administration Fee 2, Bank Fee, Visa Fee, dan SEVIS Fee.",
  },
  {
    id: "7",
    title: "Interview Visa",
    description:
      "NIEC memberikan kisi-kisi, simulasi interview, dan pembekalan sebelum wawancara di Kedutaan/Konsulat AS.",
  },
  {
    id: "8",
    title: "Pembayaran Administration Fee 2",
    description:
      "Dilakukan hanya setelah visa dinyatakan disetujui (Visa Granted).",
  },
  {
    id: "9",
    title: "Departure Briefing",
    description:
      "Pembekalan terakhir sebelum keberangkatan menuju Amerika Serikat.",
  },
];
const leftSteps = steps.slice(0, 5);

const masterClasses = [
  "Future Career",
  "International Student Profile (CV)",
  "Writing Skill (Essay)",
  "Interview Techniques",
  "Financial Literacy",
  "Moving to a New Country & Culture Shock",
  "Talent Mapping & Workshop",
  "Introduction to English Tests",
  "Visa Processing",
  "Skill Academy (Hospitality & Entrepreneur)",
  "Social Media Content & Personal Branding",
  "Celebration & Social Project",
];

const timeline = [
  { date: '11 Sep – 20 Okt', month: '', year: '2026', label: 'Periode Pendaftaran' },
  { date: '28', month: 'Sep', year: '2026', label: 'English Placement Test + Pre-screening Interview by NIEC' },
  { date: '30', month: 'Okt', year: '2026', label: 'Pembekalan & Re-training Interview by NIEC' },
  { date: '02', month: 'Nov', year: '2026', label: 'Interview with The Broadmoor (NIEC Denpasar)' },
];

const galleryImages = [
  { src: '/img/regis-1.webp', alt: 'St. Regis Aspen 1' },
  { src: '/img/regis-2.webp', alt: 'St. Regis Aspen 2' },
  { src: '/img/regis-3.webp', alt: 'St. Regis Aspen 3' },
  { src: '/img/regis-4.webp', alt: 'St. Regis Aspen 4' },
];

/* ── COMPONENT ── */

const J1Section = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section id="j1-program" className="j1-section">

      {/* ── HERO ── */}
      <div className="j1-hero">
        <div className="j1-hero-overlay" />
        <div className="j1-hero-pattern" />

        {/* ── PROMO BADGE ── */}
        <motion.div
          className="promo-badge"
          animate={{
            y: [0, -14, 0],
            rotate: [0, 0.4, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {/* Chip label — ribbon/sticker style on left side */}

          <motion.div
            whileHover={{
              scale: 1.03,
              y: -6,
              rotate: -0.5,
            }}
          >
            <div className="promo-chip-wrap">
              <span className="promo-chip">The Broadmoor, Colorado</span>
            </div>

            <span className="promo-card-wrap">
              <img
                src="/img/card-promo.webp"
                alt="The Broadmoor J-1 Internship Pass"
                className="promo-card-img"
                draggable={false}
              />
              <a
                href="#j1-requirements"
                className="promo-cta-overlay"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('j1-requirements')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                aria-label="Lihat Timeline Pendaftaran The Broadmoor"
              >
                <span className="promo-cta-shimmer" />
                Lihat Pendaftaran
                <PiCursorClickFill size={25} />
              </a>
            </span>
          </motion.div>
        </motion.div>

        <div className="container j1-hero-content">

          <motion.div
            className="j1-hero-eyebrow"
            variants={fadeLeft}
            initial="hidden"
            animate="show"
          >
            <span className="j1-badge">NIEC Indonesia</span>
            <span className="j1-badge j1-badge-outline">J1 Visa Program</span>
            <span className="j1-badge j1-badge-outline">
              <i className="fa-solid fa-location-dot" /> United States of America
            </span>
          </motion.div>

          <motion.h1
            className="j1-hero-headline"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.15 }}
          >
            Internship &amp; Training<br />
            <span className="j1-hero-highlight">di Hotel Bintang 5 Amerika</span>
          </motion.h1>

          <motion.p
            className="j1-hero-sub"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.28 }}
          >
            Raih pengalaman kerja profesional di hotel mewah kelas dunia. Dapatkan penghasilan
            kompetitif, bangun jaringan global, dan jadikan kariermu tak tertandingi bersama{' '}
            <strong>NIEC Indonesia</strong>.
          </motion.p>

          {/* Stats pill row */}
          <motion.div
            className="j1-hero-stats"
            variants={staggerContainer(0.12, 0.38)}
            initial="hidden"
            animate="show"
          >
            {[
              { icon: 'fa-solid fa-dollar-sign', text: '$14.81-$19/jam' },
              { icon: 'fa-solid fa-clock', text: '6-18 Bulan' },
              { icon: 'fa-solid fa-hotel', text: 'Hotel Bintang 5 USA' },
              { icon: 'fa-solid fa-users', text: '1000+ Students' },
            ].map((s, i) => (
              <motion.span key={i} className="j1-stat-pill" variants={scaleFade}>
                <i className={s.icon} /> {s.text}
              </motion.span>
            ))}
          </motion.div>

          <motion.div
            className="j1-hero-cta"
            variants={staggerContainer(0.14, 0.58)}
            initial="hidden"
            animate="show"
          >
            <motion.div variants={fadeUp}>
              <a
                href="https://wa.me/6281138914770"
                target="_blank"
                rel="noreferrer"
                className="j1-btn-primary"
              >
                <i className="fa-brands fa-whatsapp" /> Konsultasi Gratis
              </a>
            </motion.div>
            <motion.div variants={fadeUp}>
              <a href="#j1-positions" className="j1-btn-ghost">
                Lihat Posisi <i className="fa-solid fa-chevron-down" />
              </a>
            </motion.div>
          </motion.div>

          <motion.p
            className="j1-hero-urgency"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.75 }}
          >
            ⚡ Hanya 25 kandidat terpilih — segera amankan posisimu!
          </motion.p>
        </div>
        <div className="j1-hero-scroll-hint">
          <i className="fa-solid fa-chevron-down" />
        </div>
      </div>

      {/* ── KEY HIGHLIGHTS ── */}
      <div className="j1-highlights-wrapper">
        <div className="container">
          <motion.div
            className="j1-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <span className="j1-section-label">Keunggulan Program</span>
            <h2 className="j1-section-title">Kenapa Pilih J1 Internship NIEC?</h2>
            <p className="j1-section-desc">
              Program J1 Internship NIEC dirancang khusus untuk memberi peserta pengalaman kerja nyata
              di industri perhotelan internasional dengan kompensasi kelas dunia.
            </p>
          </motion.div>

          <motion.div
            className="j1-highlights-grid"
            variants={staggerContainer(0.12, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {highlights.map((h, i) => (
              <motion.div key={i} className="j1-highlight-card" variants={scaleFade}>
                <div className="j1-highlight-emoji">{h.emoji}</div>
                <h3 className="j1-highlight-title">{h.title}</h3>
                <p className="j1-highlight-desc">{h.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── POSITIONS ── */}
      <div id="j1-positions" className="j1-positions-wrapper">
        <div className="container">
          <motion.div
            className="j1-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <span className="j1-section-label">Pilihan Posisi</span>
            <h2 className="j1-section-title">Posisi yang Tersedia</h2>
          </motion.div>


          {/* Position Selector Buttons */}
          <motion.div
            className="j1-tab-row"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {positions.map((p, i) => (
              <button
                key={i}
                className={`j1-tab-btn${activeTab === i ? ' j1-tab-active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {p.badge}: {p.title}
              </button>
            ))}
          </motion.div>

          {/* Revamped Position Cards Grid */}
          <motion.div
            className="j1-positions-grid"
            variants={staggerContainer(0.18, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {positions.map((p, i) => {
              const divisiVal = p.details.find((d) => d.label === 'Divisi')?.value || p.badge;
              return (
                <motion.div
                  key={i}
                  className={`program-card program-card-revamped${activeTab === i ? ' program-card-active' : ''}`}
                  onClick={() => setActiveTab(i)}
                  variants={scaleFade}
                >
                  <h3 className="program-title">{p.title}</h3>
                  <div className="program-division-tag">
                    <i className="fa-solid fa-layer-group" /> Divisi: <strong>{divisiVal}</strong>
                  </div>
                  <p className="program-tagline">{p.tagline}</p>
                </motion.div>
              );
            })}
          </motion.div>

                    {/* General Program Info Banner */}
          <motion.div
            className="j1-specs-banner"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <div className="j1-specs-banner-header">
              <span className="j1-specs-badge">
                Ringkasan benefit program
              </span>
              <p className="j1-specs-subtext">Benefit berikut berlaku untuk semua posisi J1 Internship.</p>
            </div>
            <div className="j1-income-spotlight">
              <div className="j1-income-icon" aria-hidden="true">
                <i className="fa-solid fa-coins" />
              </div>
              <div className="j1-income-copy">
                <span className="j1-income-eyebrow">Allowance $14.81–$19/jam · hingga $29.184/tahun</span>
                <div className="j1-income-amount-row">
                  <strong>Rp 514.222.080</strong>
                  <span>/ tahun</span>
                </div>
                <p>*Estimasi berdasarkan kurs USD Rp17.620 pada 4 September 2026.</p>
              </div>
            </div>

            <div className="j1-specs-grid">
              <div className="j1-spec-card">
                <div className="j1-spec-icon-box">
                  <i className="fa-solid fa-clock" />
                </div>
                <div className="j1-spec-content">
                  <span className="j1-spec-label">Durasi Program</span>
                  <span className="j1-spec-value">5 – 18 Bulan</span>
                </div>
              </div>

              <div className="j1-spec-card">
                <div className="j1-spec-icon-box">
                  <i className="fa-solid fa-dollar-sign" />
                </div>
                <div className="j1-spec-content">
                  <span className="j1-spec-label">Allowance / Jam</span>
                  <span className="j1-spec-value">$14.81 – $19 / jam</span>
                </div>
              </div>

              <div className="j1-spec-card">
                <div className="j1-spec-icon-box">
                  <i className="fa-solid fa-hotel" />
                </div>
                <div className="j1-spec-content">
                  <span className="j1-spec-label">Tipe Hotel</span>
                  <span className="j1-spec-value">Bintang 5 Amerika</span>
                </div>
              </div>

            </div>
          </motion.div>

        </div>
      </div>

      {/* ── GALLERY ── */}
      {/* <div className="j1-gallery-wrapper">
        <div className="container">
          <motion.div
            className="j1-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <span className="j1-section-label">Galeri Program</span>
            <h2 className="j1-section-title">Suasana Kerja di St. Regis Aspen</h2>
            <p className="j1-section-desc">
              Bayangkan dirimu bekerja di salah satu hotel paling bergengsi di Amerika Serikat.
            </p>
          </motion.div>

          <motion.div
            className="j1-gallery-grid"
            variants={staggerContainer(0.15, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {galleryImages.map((img, i) => (
              <motion.div
                key={i}
                className="j1-gallery-item"
                variants={i % 2 === 0 ? fadeLeft : fadeRight}
              >
                <img src={img.src} alt={img.alt} className="j1-gallery-img" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div> */}

      {/* ── STEPS / JOURNEY ── */}
      <div className="j1-steps-wrapper">
        <div className="j1-steps-bg-pattern" />
        <div className="container">
          <div className='row gap-2'>
            <motion.div
              className="j1-section-header col-md-4 order-2 order-md-1"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <img className="img-fluid j1-steps-img" src="/img/step-ji1.webp" alt="j1 internship steps" />
            </motion.div>

            <motion.div
              className="j1-steps-timeline col order-1 order-md-2"
              variants={staggerContainer(0.1, 0.1)}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <div>
                <span className="j1-section-label j1-section-label-light">Proses Program</span>
              </div>
              <h2 className="j1-section-title j1-title-white">
                Langkah Demi Langkah<br />
                <span className="j1-steps-headline-gold">Menuju Program J-1</span>
              </h2>
              <p className="j1-req-subtitle">
                NIEC akan mendampingi setiap proses mulai dari persiapan dokumen hingga keberangkatan.
              </p>
              <div className='row'>
                <div className='col-md-6'>
                  {steps.slice(0, 5).map((step, index) => (
                    <motion.div
                      key={step.id}
                      className="j1-step-item"
                      variants={fadeUp}
                    >
                      {/* Left number + line */}
                      <div className="j1-step-timeline-col">
                        <div className="j1-step-circle">{(step.id).toString().padStart(2, '0')}</div>
                      </div>

                      {/* Content */}
                      <div className="j1-step-body">
                        <div className="j1-step-body-inner">
                          {/* <div className="j1-step-badge">Langkah {index + 1}</div> */}
                          <h4 className="j1-step-title">{step.title}</h4>
                          {/* <p className="j1-step-desc">{step.description}</p> */}
                        </div>
                      </div>
                      <div className='d-flex justify-content-center'>
                        {step.id < steps.length && <div className={`j1-step-line ${index === leftSteps.length - 1 ? "hide-md-up" : ""}`} />}
                      </div>
                    </motion.div>
                  ))}
                </div>
                <div className='col-md-6'>
                  {steps.slice(5, 9).map((step) => (
                    <motion.div
                      key={step.id}
                      className="j1-step-item"
                      variants={fadeUp}
                    >
                      {/* Left number + line */}
                      <div className="j1-step-timeline-col">
                        <div className="j1-step-circle">{(step.id).toString().padStart(2, '0')}</div>
                      </div>

                      {/* Content */}
                      <div className="j1-step-body">
                        <div className="j1-step-body-inner">
                          {/* <div className="j1-step-badge">Langkah {index + 1}</div> */}
                          <h4 className="j1-step-title">{step.title}</h4>
                          {/* <p className="j1-step-desc">{step.description}</p> */}
                        </div>
                      </div>

                      <div className='d-flex justify-content-center'>
                        {step.id < steps.length && <div className="j1-step-line" />}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ── MEMBER EXCLUSIVE ── */}
      <div className="j1-member-wrapper">
        <div className="container">
          <div className="j1-member-inner">

            {/* LEFT */}
            <motion.div
              className="j1-member-left"
              variants={fadeLeft}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <span className="j1-member-exclusive-badge">
                <i className="fa-solid fa-crown" /> MEMBER EXCLUSIVE
              </span>

              <h2 className="j1-section-title j1-title-white" style={{ marginTop: '20px' }}>
                Jadilah Member NIEC &amp; Dapatkan Akses ke
                <span className="j1-steps-headline-gold"> 12 Seri Master Class</span>
              </h2>

              <p className="j1-req-subtitle">
                Persiapkan studi, karier, dan masa depan internasionalmu bersama mentor berpengalaman.
                Seluruh kelas dirancang untuk membekali kamu sebelum berangkat kuliah maupun bekerja ke luar negeri.
              </p>

              <div className="j1-member-perks">
                <div className="j1-member-perk">
                  <div className="j1-member-perk-icon"><i className="fa-solid fa-chalkboard-user" /></div>
                  <div>
                    <p className="j1-member-perk-title">Mentor Profesional</p>
                    <p className="j1-member-perk-sub">Dibimbing langsung oleh pakar industri internasional</p>
                  </div>
                </div>
                <div className="j1-member-perk">
                  <div className="j1-member-perk-icon"><i className="fa-solid fa-gem" /></div>
                  <div>
                    <p className="j1-member-perk-title">Materi Eksklusif</p>
                    <p className="j1-member-perk-sub">Konten tidak tersedia di tempat lain — hanya untuk member NIEC</p>
                  </div>
                </div>
                <div className="j1-member-perk">
                  <div className="j1-member-perk-icon"><i className="fa-solid fa-certificate" /></div>
                  <div>
                    <p className="j1-member-perk-title">Sertifikat Resmi</p>
                    <p className="j1-member-perk-sub">Dapatkan sertifikat yang diakui untuk memperkuat profilmu</p>
                  </div>
                </div>
              </div>

              {/* <a
                href="https://wa.me/6281138914770"
                target="_blank"
                rel="noreferrer"
                className="j1-btn-primary"
                style={{ marginTop: '8px', display: 'inline-flex' }}
              >
                <i className="fa-solid fa-crown" /> Daftar Member Sekarang
              </a> */}
            </motion.div>

            {/* RIGHT */}
            <motion.div
              className="j1-member-right"
              variants={staggerContainer(0.08, 0.2)}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <div className="j1-masterclass-grid">
                {masterClasses.map((item, index) => (
                  <motion.div
                    key={index}
                    className="j1-masterclass-card"
                    variants={scaleFade}
                  >
                    <div className="j1-masterclass-num">{(index + 1).toString().padStart(2, '0')}</div>
                    <p className="j1-masterclass-title">{item}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </div>

      {/* ── REQUIREMENTS & TIMELINE ── */}
      <div id="j1-requirements" className="j1-requirements-wrapper">
        <div className="container">
          <div className="j1-req-inner">

            {/* Left — requirements */}
            <motion.div
              className="j1-req-left"
              variants={fadeLeft}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <span className="j1-section-label j1-section-label-light">Persyaratan</span>
              <h2 className="j1-section-title j1-title-white">Persyaratan Peserta</h2>
              <p className="j1-req-subtitle">
                Pastikan kamu memenuhi seluruh persyaratan berikut sebelum mendaftar.
              </p>
              <motion.div
                className="j1-req-grid"
                variants={staggerContainer(0.12, 0.2)}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
              >
                {requirements.map((r, i) => (
                  <motion.div key={i} className="j1-req-card" variants={scaleFade}>
                    <div className="j1-req-icon">
                      <i className={r.icon} />
                    </div>
                    <div>
                      <p className="j1-req-label">{r.label}</p>
                      <p className="j1-req-detail">{r.detail}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right — timeline */}
            <motion.div
              className="j1-req-right"
              variants={fadeRight}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <span className="j1-section-label j1-section-label-light">Jadwal Seleksi</span>
              <h2 className="j1-section-title j1-title-white">Timeline Pendaftaran</h2>
              <p className="j1-req-subtitle">
                Ikuti setiap tahapan seleksi untuk memastikan keberhasilanmu.
              </p>
              <motion.div
                className="j1-timeline-list"
                variants={staggerContainer(0.12, 0.2)}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
              >
                {timeline.map((t, i) => (
                  <motion.div key={i} className="j1-timeline-item" variants={scaleFade}>
                    <div className="j1-timeline-date-block">
                      <span className="j1-timeline-date">{t.date}</span>
                      <span className="j1-timeline-month">{t.month} {t.year}</span>
                    </div>
                    <div className="j1-timeline-connector">
                      <div className="j1-timeline-dot" />
                      {i < timeline.length - 1 && <div className="j1-timeline-line" />}
                    </div>
                    <div className="j1-timeline-label">{t.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div id="j1-register" className="j1-cta-wrapper">
        <div className="container">
          <motion.div
            className="j1-cta-inner"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <span className="j1-section-label">Langkah Pertamamu</span>
            <h2 className="j1-cta-title">
              Siap Berkarier<br />di Amerika Serikat?
            </h2>
            <p className="j1-cta-desc">
              Hanya 25 kandidat terbaik yang akan dipilih. Jangan lewatkan kesempatan langka ini —
              daftarkan dirimu sekarang dan mulailah perjalanan karier internasionalmu bersama{' '}
              <strong>NIEC Indonesia</strong>.
            </p>
            <motion.div
              className="j1-cta-actions"
              variants={staggerContainer(0.15, 0.2)}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <motion.a
                href="https://wa.me/6281138914770?text=Halo%2C%20saya%20ingin%20konsultasi%20program%20J1%20Internship%20NIEC"
                target="_blank"
                rel="noreferrer"
                className="j1-btn-primary j1-btn-lg"
                variants={scaleFade}
              >
                <i className="fa-brands fa-whatsapp" /> Konsultasi Gratis
              </motion.a>
              <motion.a
                href="/#register"
                className="j1-btn-outline j1-btn-lg"
                variants={scaleFade}
              >
                Daftar Sekarang
              </motion.a>
            </motion.div>
            <p className="j1-cta-note">
              🔒 Data kamu aman. Kami tidak akan membagikan informasi pribadimu.
            </p>
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default J1Section;
